import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as LLMVendorModelsAPI from 'api/LLMVendorModels';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';
import { PAGES } from 'sections/LLMVendorModels/constants';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  selectedPage: PAGES.MAIN_PAGE,
  asrvendor: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Description', key: 'description', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Website', key: 'website', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Help Text', key: 'help_text', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  asrvendorModels: {
    selectedVendor: null,
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Model_Key', key: 'model_key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getASRVendorData = createAsyncThunk('asrVendorModels/getASRVendorData', async ({ params, signal }) => {
  const res = await LLMVendorModelsAPI.getASRVendorData(params, signal);
  return res?.data?.data;
});

const createASRVendor = createAsyncThunk('asrVendorModels/createASRVendor', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.createASRVendor(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateASRVendor = createAsyncThunk('asrVendorModels/updateASRVendor', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.updateASRVendor(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteASRVendor = createAsyncThunk('asrVendorModels/deleteASRVendor', async (id) => {
  try {
    const res = await LLMVendorModelsAPI.deleteASRVendor(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

// Super-admin DRF list responses are unwrapped — reducers below
// consume ``action.payload.count`` / ``action.payload.results``.
const getASRVendorModelsData = createAsyncThunk('asrVendorModels/getASRVendorModelsData', async ({ params, signal }) => {
  const res = await LLMVendorModelsAPI.getASRVendorModelsData(params, signal);
  return res?.data;
});

const createASRVendorModels = createAsyncThunk('asrVendorModels/createASRVendorModels', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.createASRVendorModels(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateASRVendorModels = createAsyncThunk('asrVendorModels/updateASRVendorModels', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.updateASRVendorModels(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteASRVendorModels = createAsyncThunk('asrVendorModels/deleteASRVendorModels', async (id) => {
  try {
    const res = await LLMVendorModelsAPI.deleteASRVendorModels(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const ASRVendorModelsSlice = createSlice({
  name: 'asrVendorModels',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    //Vendor Tab
    setVendorCurrentPage: (state, action) => {
      state.asrvendor.pagination.currentPage = action.payload;
    },
    setVendorPageSize: (state, action) => {
      state.asrvendor.pagination.pageSize = action.payload;
    },
    setSelectedVendorData: (state, action) => {
      state.asrvendorModels.selectedVendor = action.payload;
    },

    //Action Point Tab
    setVendorModelsCurrentPage: (state, action) => {
      state.asrvendorModels.pagination.currentPage = action.payload;
    },
    setVendorModelsPageSize: (state, action) => {
      state.asrvendorModels.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getASRVendorData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getASRVendorData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.asrvendor.pagination.totalRecords = action.payload?.count;
      state.asrvendor.data = action.payload?.results || [];
    },
    [getASRVendorData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createASRVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createASRVendor.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createASRVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateASRVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateASRVendor.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.asrvendor.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.asrvendor.data = updatedData;
    },

    [updateASRVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteASRVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteASRVendor.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteASRVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [getASRVendorModelsData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getASRVendorModelsData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.asrvendorModels.pagination.totalRecords = action.payload?.count;
      state.asrvendorModels.data = action.payload?.results || [];
    },
    [getASRVendorModelsData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createASRVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createASRVendorModels.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createASRVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateASRVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateASRVendorModels.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.asrvendorModels.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.asrvendorModels.data = updatedData;
    },

    [updateASRVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteASRVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteASRVendorModels.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteASRVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const {
  resetState,
  setSelectedPage,
  setVendorCurrentPage,
  setVendorPageSize,
  setSelectedVendorData,
  setVendorModelsCurrentPage,
  setVendorModelsPageSize
} = ASRVendorModelsSlice.actions;

export default ASRVendorModelsSlice.reducer;

export {
  resetState,
  setSelectedPage,
  //Vendor
  setVendorCurrentPage,
  setVendorPageSize,
  setSelectedVendorData,
  getASRVendorData,
  createASRVendor,
  updateASRVendor,
  deleteASRVendor,
  //Vendor Models
  setVendorModelsCurrentPage,
  setVendorModelsPageSize,
  getASRVendorModelsData,
  createASRVendorModels,
  updateASRVendorModels,
  deleteASRVendorModels
};
